import { useEffect, useState } from 'react';
import { Topbar } from '@/components/Topbar';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ConnectMetaButton } from '@/components/facebook/ConnectMetaButton';
import { facebookAdAccountService, MetaAdAccount, SyncablePage } from '@/services/facebook-ad-account.service';
import { ConnectedAdAccount } from '@/services/meta-oauth.service';
import { Star, Trash2, Power, Globe, Camera, Link as LinkIcon, RefreshCw, X } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

type Props = {
  /** Rendered inside FacebookAccountsManager's tab shell — suppress the page Topbar. */
  embedded?: boolean;
};

export default function FacebookAccountsList({ embedded = false }: Props = {}) {
  const [accounts, setAccounts] = useState<MetaAdAccount[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [del, setDel] = useState<MetaAdAccount | null>(null);
  const [picker, setPicker] = useState<{ acc: MetaAdAccount; pages: SyncablePage[] } | null>(null);

  const refresh = async () => {
    try { setAccounts(await facebookAdAccountService.list()); }
    catch { setAccounts([]); }
  };
  useEffect(() => { refresh(); }, []);

  /**
   * Called after the OAuth popup finalises an ad account. Pages / Instagram /
   * Business Manager are pulled immediately, because the Page tokens that carry
   * the freshly-granted scopes only exist on a token fetched AFTER consent —
   * and a stale Instagram id makes Meta reject creatives later.
   * Best-effort: a failure here must not undo a successful connect.
   */
  const onConnected = async (acc: ConnectedAdAccount) => {
    await refresh();
    try {
      const r = await facebookAdAccountService.syncResources(acc.accountUid);
      if (r.requiresChoice) {
        // Multiple Pages — let the user choose rather than guessing.
        setPicker({ acc: { ...(acc as unknown as MetaAdAccount) }, pages: r.availablePages });
      } else {
        await refresh();
      }
    } catch {
      /* non-fatal — the Sync button on the card retries */
    }
  };

  const onSetDefault = async (acc: MetaAdAccount) => {
    if (acc.isDefault) return;
    setBusy(acc.accountUid);
    try {
      await facebookAdAccountService.setDefault(acc.accountUid);
      toast.success(`${acc.adAccountName} is now the default account`);
      await refresh();
    } catch (e: any) { toast.error(e?.response?.data?.message ?? 'Failed to set default'); }
    finally { setBusy(null); }
  };

  const onToggleActive = async (acc: MetaAdAccount) => {
    setBusy(acc.accountUid);
    try {
      await facebookAdAccountService.setActive(acc.accountUid, !acc.isActive);
      toast.success(`${acc.adAccountName} ${!acc.isActive ? 'activated' : 'deactivated'}`);
      await refresh();
    } catch (e: any) { toast.error(e?.response?.data?.message ?? 'Failed to toggle'); }
    finally { setBusy(null); }
  };

  const onSync = async (acc: MetaAdAccount, pageId?: string) => {
    setBusy(acc.accountUid);
    try {
      const r = await facebookAdAccountService.syncResources(acc.accountUid, pageId);
      if (r.requiresChoice) {
        setPicker({ acc, pages: r.availablePages });
        return;
      }
      const parts: string[] = [];
      if (r.selectedPage) parts.push(`Page "${r.selectedPage.name}"`);
      if (r.selectedPage?.instagramAccount) parts.push(`IG @${r.selectedPage.instagramAccount.username ?? r.selectedPage.instagramAccount.id}`);
      if (r.businessManagerId) parts.push(`BM ${r.businessManagerId}`);
      toast.success(parts.length > 0 ? `Synced ${parts.join(' · ')}` : 'No Pages found for this Meta user');
      setPicker(null);
      await refresh();
    } catch {
      /* axios interceptor toasts */
    } finally {
      setBusy(null);
    }
  };

  const onRemove = async () => {
    if (!del) return;
    setBusy(del.accountUid);
    try {
      await facebookAdAccountService.remove(del.accountUid);
      toast.success(`${del.adAccountName} removed`);
      setDel(null);
      await refresh();
    } catch (e: any) { toast.error(e?.response?.data?.message ?? 'Failed to remove'); }
    finally { setBusy(null); }
  };

  const connectButton = (
    <ConnectMetaButton label="Connect with Facebook" onConnected={onConnected} />
  );

  return (
    <>
      {embedded ? (
        <div className="flex justify-end px-7 pt-4">{connectButton}</div>
      ) : (
        <Topbar
          title="Facebook Ad Accounts"
          breadcrumb="Facebook Ads"
          description="Manage and switch between connected Meta ad accounts."
          right={connectButton}
        />
      )}

      <div className={clsx('w-full space-y-5 px-7', embedded ? 'pb-24 pt-4' : 'py-7')}>
        {accounts === null && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="skeleton h-40 rounded-2xl" />
            <div className="skeleton h-40 rounded-2xl" />
          </div>
        )}

        {accounts && accounts.length === 0 && (
          <EmptyState
            icon={<Globe size={36} className="text-text-muted" />}
            title="No ad accounts yet"
            description="Connect a Meta ad account to start managing campaigns. You can connect multiple accounts and switch between them."
            action={connectButton}
          />
        )}

        {accounts && accounts.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2">
            {accounts.map((acc) => (
              <AccountCard
                key={acc.accountUid}
                acc={acc}
                busy={busy === acc.accountUid}
                onSetDefault={() => onSetDefault(acc)}
                onToggleActive={() => onToggleActive(acc)}
                onSync={() => onSync(acc)}
                onDelete={() => setDel(acc)}
              />
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!del}
        title="Remove Meta ad account?"
        message={del ? `Disconnect ${del.adAccountName} (${del.adAccountId})? Existing campaign data on Meta is unaffected; SlightorCRM just stops syncing this account.` : ''}
        danger
        onConfirm={onRemove}
        onCancel={() => setDel(null)}
      />

      <PagePickerModal
        picker={picker}
        busy={!!picker && busy === picker.acc.accountUid}
        onCancel={() => setPicker(null)}
        onPick={(pageId) => picker && onSync(picker.acc, pageId)}
      />
    </>
  );
}

interface PagePickerModalProps {
  picker: { acc: MetaAdAccount; pages: SyncablePage[] } | null;
  busy: boolean;
  onCancel: () => void;
  onPick: (pageId: string) => void;
}

function PagePickerModal({ picker, busy, onCancel, onPick }: PagePickerModalProps) {
  if (!picker) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-lg rounded-2xl bg-surface shadow-elevated">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <h3 className="font-display text-base font-semibold text-text-primary">Pick a Facebook Page</h3>
            <p className="mt-0.5 text-xs text-text-muted">
              The Page (and its linked Instagram) will be saved to <strong>{picker.acc.adAccountName}</strong>.
            </p>
          </div>
          <button onClick={onCancel} className="rounded-lg p-1.5 text-text-muted hover:bg-surface-alt hover:text-text-primary"><X size={16} /></button>
        </div>
        <div className="max-h-96 overflow-y-auto p-3">
          {picker.pages.map((p) => (
            <button
              key={p.id}
              disabled={busy}
              onClick={() => onPick(p.id)}
              className="flex w-full items-center gap-3 rounded-lg border border-transparent px-3 py-2.5 text-left transition-colors hover:border-border hover:bg-surface-alt disabled:cursor-not-allowed disabled:opacity-60"
            >
              {p.pictureUrl ? (
                <img src={p.pictureUrl} alt="" className="h-10 w-10 rounded-full" />
              ) : (
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-info-soft text-info"><LinkIcon size={16} /></div>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold text-text-primary">{p.name}</div>
                <div className="font-mono text-xs text-text-muted">{p.id}</div>
                {p.instagramAccount && (
                  <div className="mt-0.5 inline-flex items-center gap-1 text-2xs text-text-muted">
                    <Camera size={10} /> @{p.instagramAccount.username ?? p.instagramAccount.id}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

interface AccountCardProps {
  acc: MetaAdAccount;
  busy: boolean;
  onSetDefault: () => void;
  onToggleActive: () => void;
  onSync: () => void;
  onDelete: () => void;
}

function AccountCard({ acc, busy, onSetDefault, onToggleActive, onSync, onDelete }: AccountCardProps) {
  return (
    <Card
      bordered
      padded={false}
      className={clsx(
        'overflow-hidden transition-shadow',
        acc.isDefault && 'ring-2 ring-primary/40',
        !acc.isActive && 'opacity-70',
      )}
    >
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-info-soft text-info">
            <Globe size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-display text-[15px] font-semibold text-text-primary truncate">
                {acc.adAccountName}
              </h4>
              {acc.isDefault && (
                <span className="inline-flex items-center gap-1 rounded-full bg-primary-soft px-2 py-0.5 text-2xs font-semibold text-primary">
                  <Star size={10} fill="currentColor" /> Default
                </span>
              )}
            </div>
            <div className="mt-0.5 font-mono text-xs text-text-muted">{acc.adAccountId}</div>
            <div className="mt-1 flex items-center gap-2 text-2xs text-text-muted">
              <span>{acc.currency}</span>
              {acc.timezoneName && <span>· {acc.timezoneName}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 border-t border-border bg-surface-alt/40 px-5 py-3 text-2xs">
        <Stat icon={<LinkIcon size={11} />} label="Page" value={acc.pageId ?? '—'} />
        <Stat icon={<Camera size={11} />} label="Instagram" value={acc.instagramAccountId ?? '—'} />
        <Stat icon={<Globe size={11} />} label="BM" value={acc.businessManagerId ?? '—'} />
      </div>

      <div className="flex items-center justify-between border-t border-border px-5 py-3">
        <button
          onClick={onToggleActive}
          disabled={busy || acc.isDefault}
          className={clsx(
            'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium transition-colors',
            acc.isActive
              ? 'bg-success-soft text-success hover:bg-success-soft/70'
              : 'bg-surface-alt text-text-muted hover:bg-border',
            (busy || acc.isDefault) && 'cursor-not-allowed opacity-60',
          )}
          title={acc.isDefault ? 'Cannot deactivate the default account' : (acc.isActive ? 'Click to deactivate' : 'Click to activate')}
        >
          <Power size={11} /> {acc.isActive ? 'Active' : 'Inactive'}
        </button>

        <div className="flex items-center gap-1">
          <button
            onClick={onSync}
            disabled={busy}
            className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-info-soft hover:text-info disabled:opacity-50"
            title="Sync Pages, Instagram & Business Manager from Meta"
          >
            <RefreshCw size={15} className={busy ? 'animate-spin' : ''} />
          </button>
          {!acc.isDefault && (
            <button
              onClick={onSetDefault}
              disabled={busy}
              className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-primary-soft hover:text-primary"
              title="Set as default"
            >
              <Star size={15} />
            </button>
          )}
          <button
            onClick={onDelete}
            disabled={busy}
            className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-danger-soft hover:text-danger"
            title="Remove account"
          >
            <Trash2 size={15} />
          </button>
        </div>
      </div>
    </Card>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 text-text-muted">
        {icon}
        <span className="uppercase tracking-wide">{label}</span>
      </div>
      <div className="mt-0.5 truncate font-mono text-text-secondary">{value}</div>
    </div>
  );
}
